import { createContext, useContext, useCallback, createElement } from 'react'

export const DispatchDialogContext = createContext<any>(null)

export const DialogCurrentJobContext = createContext<any>(null)

export const useConfirmation = () => useContext(DispatchDialogContext)

type Message = string | string[] | import('react').ReactNode
export type JobExtendAttrs = {
  message: Message
  noClose?: boolean
}

export type Job = Omit<
  import('components/dialog/Base').DialogBaseProps & JobExtendAttrs,
  'open'
>

export type State = {
  jobs: Job[]
  preJob: Job | null
}

export type Action = {
  type: string
  payload?: Job
}

export const actions = {
  addJob(job: Job) {
    return {
      type: 'pushBack',
      payload: job,
    } as Action
  },
  finishJob() {
    return { type: 'popFront' } as Action
  },
}

export function disableJob(preJob: Job) {
  const { cancelProps = {}, actionProps = {} } = preJob

  const disabledJob = {
    cancelProps: { ...cancelProps, disabled: true },
    actionProps: { ...actionProps, disabled: true },
    ...preJob,
  }

  return disabledJob
}

export function useDialogDispatch() {
  return useContext(DispatchDialogContext)
}

export function formatMessage(message: Message) {
  if (!Array.isArray(message)) {
    return message
  }

  const sentences = message.map((sentence) =>
    createElement('p', { key: sentence }, sentence),
  )
  return sentences
}

export default function useDialog() {
  const dispatch = useContext(DispatchDialogContext)
  /** @type { (job: Job) => void } */
  const addJob = useCallback(
    (job) => {
      ;(dispatch(actions.addJob(job)), [dispatch])
    },
    [dispatch],
  )

  return addJob
}

type AlertParams = Omit<Job, 'onAction'>
export function useAlert() {
  const dialog = useDialog()

  const alert = useCallback(
    ({ title, message, ...rest }: AlertParams) =>
      new Promise((resolve) => {
        dialog({
          title,
          message,
          actionText: '確認',
          ...rest,
          onAction: () => {
            resolve(undefined)
          },
        })
      }),
    [dialog],
  )

  return alert
}

type ConfirmParams = Omit<Job, 'onAction'> & {
  onConfirm?: () => void | Promise<any>
}
type ConfirmResult = { result: any; status: 'confirmed' | 'canceled' }

export function useConfirm() {
  const dialog = useDialog()

  const confirm: (params: ConfirmParams) => Promise<ConfirmResult> =
    useCallback(
      ({ onConfirm, onCancel, message, ...rest }) =>
        new Promise((resolve) => {
          const handleConfirm = async () => {
            let result
            if (onConfirm) {
              result = await onConfirm()
            }
            resolve({
              status: 'confirmed',
              result,
            })
          }

          const handleCancel = async () => {
            let result
            if (onCancel) {
              result = await onCancel()
            }
            resolve({
              status: 'canceled',
              result,
            })
          }

          dialog({
            noClose: true,
            onAction: handleConfirm,
            onCancel: handleCancel,
            message,
            ...rest,
          })
        }),
      [dialog],
    )

  return confirm
}
